import './ChartTable.css';
import React from 'react';
import { getGanWuxing, getZhiWuxing, getWuxingColor } from '../utils/wuxing-calculator';
import { getShishen, getShishenColorBySource } from '../utils/shishen-calculator';
import {
  calculateDiShi,
  calculateNaYin,
  getDiShiColor,
  getNaYinColor,
  getZhiCangGan,
  getXunKong
} from '../utils/bazi-utils';
import TermTip from './TermTip';

/**
 * 标准排盘表（行序按行业事实标准，专业用户靠肌肉记忆读盘）：
 * 主星 / 天干 / 地支 / 藏干 / 副星 / 星运 / 空亡 / 纳音
 * 四柱（年月日时）始终显示，日柱整列高亮；大运/流年/流月由时间轴钻取驱动，选中即作第 5/6/7 列亮出。
 */
const ChartTable = ({ baziResult, riGan, diShiData, naYinData, dayunCol, liunianCol, liuyueCol }) => {
  if (!baziResult) return null;

  const pillarCols = ['year', 'month', 'day', 'hour'].map((key) => {
    const p = baziResult[`${key}Pillar`] || {};
    return {
      key,
      title: { year: '年柱', month: '月柱', day: '日柱', hour: '时柱' }[key],
      gan: p.gan || '',
      zhi: p.zhi || '',
      isDay: key === 'day',
      // 四柱沿用页面既有 lunar 库结果，保持排盘输出不变
      dishi: diShiData ? diShiData[key] : undefined,
      nayin: naYinData ? naYinData[key] : undefined
    };
  });

  const drillCols = [];
  if (dayunCol) drillCols.push({ key: 'dayun', title: '大运', sub: `${dayunCol.gan}${dayunCol.zhi}`, gan: dayunCol.gan, zhi: dayunCol.zhi, isDrill: true });
  if (liunianCol) drillCols.push({ key: 'liunian', title: '流年', sub: `${liunianCol.year}`, gan: liunianCol.gan, zhi: liunianCol.zhi, isDrill: true });
  if (liuyueCol) drillCols.push({ key: 'liuyue', title: '流月', sub: liuyueCol.monthName, gan: liuyueCol.gan, zhi: liuyueCol.zhi, isDrill: true });

  const cols = [...pillarCols, ...drillCols];
  const n = cols.length;

  const cellClass = (c) => `pc-cell${c.isDay ? ' pc-day' : ''}${c.isDrill ? ' pc-drill' : ''}`;

  const renderMainStar = (c) => {
    if (c.isDay) {
      return <TermTip term="日元" className="pc-star" style={{ color: getShishenColorBySource('日元', c.gan) }} />;
    }
    const ss = getShishen(riGan, c.gan);
    if (!ss) return <span className="pc-dim">—</span>;
    return <TermTip term={ss} className="pc-star" style={{ color: getShishenColorBySource(ss, c.gan) }} />;
  };

  const renderHidden = (c) => {
    const hidden = (getZhiCangGan(c.zhi) || []).filter(Boolean);
    if (!hidden.length) return <span className="pc-dim">—</span>;
    return (
      <div className="pc-hidden">
        {hidden.map((g, i) => (
          <span key={i} className="pc-hidden-gan" style={{ color: getWuxingColor(getGanWuxing(g)) }}>{g}</span>
        ))}
      </div>
    );
  };

  const renderSubStar = (c) => {
    const hidden = (getZhiCangGan(c.zhi) || []).filter(Boolean);
    if (!hidden.length) return <span className="pc-dim">—</span>;
    return (
      <div className="pc-substar">
        {hidden.map((g, i) => {
          const ss = getShishen(riGan, g);
          return (
            <TermTip key={i} term={ss} className="pc-sub" style={{ color: getShishenColorBySource(ss, g) }} />
          );
        })}
      </div>
    );
  };

  const renderDishi = (c) => {
    const d = c.dishi != null && c.dishi !== '' ? c.dishi : calculateDiShi(riGan, c.zhi);
    if (!d) return <span className="pc-dim">—</span>;
    return <TermTip term={d} className="pc-dishi" style={{ color: getDiShiColor(d) }} />;
  };

  const renderKong = (c) => {
    const kong = getXunKong(c.gan, c.zhi);
    if (!kong.length) return <span className="pc-dim">—</span>;
    return <span className="pc-kong">{kong.join('')}</span>;
  };

  const renderNayin = (c) => {
    const ny = c.nayin != null && c.nayin !== '' ? c.nayin : calculateNaYin(c.gan, c.zhi);
    if (!ny) return <span className="pc-dim">—</span>;
    return <TermTip nayin className="pc-nayin" style={{ color: getNaYinColor(ny) }}>{ny}</TermTip>;
  };

  const rows = [
    { label: '主星', render: renderMainStar },
    { label: '天干', render: (c) => <span className="pc-char" style={{ color: getWuxingColor(getGanWuxing(c.gan)) }}>{c.gan}</span> },
    { label: '地支', render: (c) => <span className="pc-char" style={{ color: getWuxingColor(getZhiWuxing(c.zhi)) }}>{c.zhi}</span> },
    { label: '藏干', render: renderHidden },
    { label: '副星', render: renderSubStar },
    { label: '星运', render: renderDishi },
    { label: '空亡', labelTip: '空亡', render: renderKong },
    { label: '纳音', render: renderNayin }
  ];

  const gridStyle = { gridTemplateColumns: `var(--pc-label-w) repeat(${n}, minmax(var(--pc-col-min), 1fr))` };

  // 行名列跟随横滚吸左：grid item 上 position:sticky 不生效（containing block
  // 是自身 grid area），改为滚动时同步 --pc-shift 做 translateX（直接写 DOM，无重渲染）
  const onScroll = (e) => {
    e.currentTarget.style.setProperty('--pc-shift', `${e.currentTarget.scrollLeft}px`);
  };

  return (
    <div className="pc-scroll" onScroll={onScroll}>
      <div className="pc-grid" style={gridStyle}>
        {/* 表头：柱位 */}
        <div className="pc-cell pc-rowlabel pc-corner" />
        {cols.map((c) => (
          <div key={`h-${c.key}`} className={`pc-cell pc-head ${cellClass(c)}`}>
            <span className="pc-head-title">{c.title}</span>
            {c.sub && <span className="pc-head-sub">{c.sub}</span>}
          </div>
        ))}

        {/* 数据行 */}
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <div className="pc-cell pc-rowlabel">
              {row.labelTip ? <TermTip term={row.labelTip}>{row.label}</TermTip> : row.label}
            </div>
            {cols.map((c) => (
              <div key={`${row.label}-${c.key}`} className={cellClass(c)}>
                {row.render(c)}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ChartTable;
